/* eslint-disable consistent-return */
import { useCallback } from 'react';
import { getSessionAPI, getAllSessionsAPI } from '../api/os';
import useStore from '../store';
import notify from '../../../utils/notify';
import { ChatMessage, ToolCall, ReasoningMessage, ChatEntry } from '../../../types/os';
// eslint-disable-next-line import/extensions
import { getJsonMarkdown } from '../../../lib/utils';
import parseToolResult from '../parseToolResult';
import useWorkspaceStore from '../../../store/workspace/workspaceStore';
import { rehydrateConnectorDrafts } from '../messages/rehydrateConnectorDrafts';

interface ChatHistoryEntry {
    content: string | null
    role: 'user' | 'assistant' | 'system' | 'tool'
    created_at: number
    from_history?: boolean
    stop_after_tool_call?: boolean
    tool_call_id?: string
    tool_name?: string
    tool_args?: Record<string, unknown>
    tool_call_error?: boolean
    tool_calls?: Array<{
        role?: string
        content?: string | null
        tool_call_id?: string
        tool_name?: string
        tool_args?: Record<string, unknown>
        tool_call_error?: boolean
        metrics?: { time?: number }
        created_at?: number
        result?: unknown
        function?: {
            name: string
            arguments: string
        }
    }>
    metrics?: {
        input_tokens?: number
        output_tokens?: number
        total_tokens?: number
    }
}

interface SessionResponse {
    session_id: string
    agent_id: string
    user_id: string | null
    runs?: ChatEntry[]
    chat_history?: ChatHistoryEntry[]
    memory: {
        runs?: ChatEntry[]
        chats?: ChatEntry[]
    }
    agent_data: Record<string, unknown>
}

interface LoaderArgs {
    entityType: 'agent' | 'team' | null
    agentId?: string | null
    teamId?: string | null
    dbId: string | null
}

const useSessionLoader = () => {
    const setMessages = useStore((state) => state.setMessages);
    const selectedEndpoint = useStore((state) => state.selectedEndpoint);
    const setIsSessionsLoading = useStore((state) => state.setIsSessionsLoading);
    const setSessionsData = useStore((state) => state.setSessionsData);
    const workspaceId =
        useWorkspaceStore((s) => s.workspace?.id) ||
        (typeof window !== 'undefined' ? localStorage.getItem('he_defws') : null);

    const getSessions = useCallback(
        async ({ entityType, agentId, teamId, dbId }: LoaderArgs) => {
            const selectedId = entityType === 'agent' ? agentId : teamId;
            if (!selectedEndpoint || !entityType || !selectedId || !dbId) return;

            try {
                setIsSessionsLoading(true);

                const sessions = await getAllSessionsAPI(
                    selectedEndpoint,
                    entityType,
                    selectedId,
                    dbId
                );
                console.log('Fetched sessions:', sessions);
                // Normalize timestamps to epoch seconds to handle ISO strings or ms
                const toEpochSeconds = (v: any): number | undefined => {
                    if (v === null || v === undefined) return undefined;
                    if (typeof v === 'number') {
                        // Treat > 1e12 as ms, otherwise seconds
                        return v > 1e12 ? Math.floor(v / 1000) : Math.floor(v);
                    }
                    if (typeof v === 'string') {
                        const asNum = Number(v);
                        if (!Number.isNaN(asNum) && v.trim() !== '') {
                            return toEpochSeconds(asNum);
                        }
                        const d = new Date(v);
                        return Number.isNaN(d.getTime()) ? undefined : Math.floor(d.getTime() / 1000);
                    }
                    return undefined;
                };

                const raw = (sessions as any)?.data ?? [];
                // Filter out sessions from builder flows (signal, skill, health model)
                // by agent_id when available, or by session_name patterns as fallback
                const EXCLUDED_AGENT_IDS = new Set([
                    'signal_builder',
                    'skill_builder',
                    'health_model_builder',
                ]);
                const EXCLUDED_NAME_PATTERNS = [
                    'I want to create a new skill from scratch.',
                    'I want to create a new signal from scratch.',
                    'Start the health model builder conversation',
                    'Let\'s update the signal ',
                    'Let\'s update the skill ',
                ];
                const isBuilderSession = (s: any): boolean => {
                    if (s.agent_id && EXCLUDED_AGENT_IDS.has(s.agent_id)) return true;
                    // Exclude headless automation agent runs from sidebar
                    if (s.agent_id && s.agent_id.startsWith('headless_')) return true;
                    if (s.session_id && s.session_id.startsWith('headless_')) return true;
                    const name = (s.session_name || '').trim();
                    if (name.startsWith('{') && name.endsWith('}')) {
                        try {
                            const parsed = JSON.parse(name);
                            if (parsed.input_content) return true;
                        } catch { /* not JSON, continue */ }
                    }
                    return EXCLUDED_NAME_PATTERNS.some(p => name.startsWith(p));
                };
                const filtered = raw.filter((s: any) => !isBuilderSession(s));
                const normalized = filtered.map((s: any) => {
                    const created = toEpochSeconds(s?.created_at);
                    const updated = toEpochSeconds(s?.updated_at);
                    return {
                        ...s,
                        created_at: created ?? Math.floor(Date.now() / 1000),
                        ...(updated !== undefined ? { updated_at: updated } : {})
                    };
                });
                setSessionsData(normalized);
            } catch {
                notify({
                    type: 'failure',
                    messageList: ['Error loading sessions'],
                });
                setSessionsData([]);
            } finally {
                setIsSessionsLoading(false);
            }
        },
        [selectedEndpoint, setSessionsData, setIsSessionsLoading]
    );

    const getSession = useCallback(
        async (
            { entityType, agentId, teamId, dbId }: LoaderArgs,
            sessionId: string
        ) => {
            const selectedId = entityType === 'agent' ? agentId : teamId;
            if (
                !selectedEndpoint ||
                !sessionId ||
                !entityType ||
                !selectedId ||
                !dbId
            ) return;

            try {
                const response: SessionResponse = await getSessionAPI(
                    selectedEndpoint,
                    entityType,
                    sessionId,
                    dbId
                );
                console.log('Fetched session:', response);
                if (response) {
                    // Handle new chat_history format
                    if (response.chat_history && Array.isArray(response.chat_history)) {
                        // Normalize timestamp to epoch seconds
                        const normalizeTs = (v: any): number => {
                            if (typeof v === 'number') {
                                return v > 1e12 ? Math.floor(v / 1000) : Math.floor(v);
                            }
                            if (typeof v === 'string') {
                                const asNum = Number(v);
                                if (!Number.isNaN(asNum) && v.trim() !== '') {
                                    return normalizeTs(asNum);
                                }
                                const d = new Date(v);
                                return Number.isNaN(d.getTime()) ? Math.floor(Date.now() / 1000) : Math.floor(d.getTime() / 1000);
                            }
                            return Math.floor(Date.now() / 1000);
                        };

                        const processedMessages: ChatMessage[] = [];
                        const history = response.chat_history.filter(msg => msg.role !== 'system');

                        for (const msg of history) {
                            // Tool role messages get attached to the preceding agent message
                            if (msg.role === 'tool') {
                                const lastAgent = [...processedMessages].reverse().find(m => m.role === 'agent');
                                if (lastAgent) {
                                    const toolCall: ToolCall = {
                                        role: 'tool',
                                        content: msg.content,
                                        tool_call_id: msg.tool_call_id || `tool-${Date.now()}`,
                                        tool_name: msg.tool_name || 'unknown',
                                        tool_args: (msg.tool_args as Record<string, string>) || {},
                                        tool_call_error: msg.tool_call_error || false,
                                        metrics: { time: 0 },
                                        created_at: normalizeTs(msg.created_at),
                                        status: 'complete',
                                        result: parseToolResult(msg.content),
                                    };
                                    if (!lastAgent.tool_calls) lastAgent.tool_calls = [];
                                    lastAgent.tool_calls.push(toolCall);
                                }
                                continue;
                            }

                            // Build the base message
                            const chatMsg: ChatMessage = {
                                role: msg.role === 'assistant' ? 'agent' : msg.role,
                                content: msg.content || '',
                                created_at: normalizeTs(msg.created_at),
                            };

                            // Attach inline tool_calls from assistant messages
                            if (msg.role === 'assistant' && msg.tool_calls && msg.tool_calls.length > 0) {
                                chatMsg.tool_calls = msg.tool_calls.map((tc): ToolCall => ({
                                    role: (tc.role as ToolCall['role']) || 'tool',
                                    content: tc.content || null,
                                    tool_call_id: tc.tool_call_id || tc.function?.name || `tool-${Date.now()}`,
                                    tool_name: tc.tool_name || tc.function?.name || 'unknown',
                                    tool_args: (tc.tool_args || (tc.function?.arguments ? (() => { try { return JSON.parse(tc.function.arguments); } catch { return {}; } })() : {})) as Record<string, string>,
                                    tool_call_error: tc.tool_call_error || false,
                                    metrics: { time: tc.metrics?.time ?? 0 },
                                    created_at: normalizeTs(tc.created_at),
                                    status: 'complete',
                                    result: parseToolResult(tc.result),
                                }));
                            }

                            processedMessages.push(chatMsg);
                        }

                        console.log('Setting messages from chat_history:', processedMessages);
                        setMessages(processedMessages);
                        // Rehydrate any pending approval draft cards — fire-and-forget, non-fatal
                        if (workspaceId) {
                            rehydrateConnectorDrafts(processedMessages, String(workspaceId))
                                .then((rehydrated) => setMessages(rehydrated))
                                .catch(() => { /* non-fatal: live card path still works */ });
                        }
                        return processedMessages;
                    }

                    // Handle legacy runs format for backward compatibility
                    if (Array.isArray(response)) {
                        const messagesFor = response.flatMap((run) => {
                            const filteredMessages: ChatMessage[] = [];

                            if (run) {
                                filteredMessages.push({
                                    role: 'user',
                                    content: run.run_input ?? '',
                                    created_at: run.created_at
                                });
                            }

                            if (run) {
                                const toolCalls = [
                                    ...(run.tools ?? []).map((t: any) => ({
                                        ...t,
                                        result: parseToolResult(t.result),
                                    })),
                                    ...(run.extra_data?.reasoning_messages ?? []).reduce(
                                        (acc: ToolCall[], msg: ReasoningMessage) => {
                                            if (msg.role === 'tool') {
                                                acc.push({
                                                    role: msg.role,
                                                    content: msg.content,
                                                    tool_call_id: msg.tool_call_id ?? '',
                                                    tool_name: msg.tool_name ?? '',
                                                    tool_args: msg.tool_args ?? {},
                                                    tool_call_error: msg.tool_call_error ?? false,
                                                    metrics: msg.metrics ?? { time: 0 },
                                                    created_at:
                                                        msg.created_at ?? Math.floor(Date.now() / 1000)
                                                });
                                            }
                                            return acc;
                                        },
                                        []
                                    )
                                ];

                                filteredMessages.push({
                                    role: 'agent',
                                    content: (run.content as string) ?? '',
                                    tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
                                    extra_data: run.extra_data,
                                    images: run.images,
                                    videos: run.videos,
                                    audio: run.audio,
                                    response_audio: run.response_audio,
                                    created_at: run.created_at
                                });
                            }
                            return filteredMessages;
                        });

                        const processedMessages = messagesFor.map(
                            (message: ChatMessage) => {
                                if (Array.isArray(message.content)) {
                                    const textContent = message.content
                                        .filter((item: { type: string }) => item.type === 'text')
                                        .map((item) => item.text)
                                        .join(' ');

                                    return {
                                        ...message,
                                        content: textContent
                                    };
                                }
                                if (typeof message.content !== 'string') {
                                    return {
                                        ...message,
                                        content: getJsonMarkdown(message.content)
                                    };
                                }
                                return message;
                            }
                        );

                        setMessages(processedMessages);
                        // Rehydrate any pending approval draft cards — fire-and-forget, non-fatal
                        if (workspaceId) {
                            rehydrateConnectorDrafts(processedMessages, String(workspaceId))
                                .then((rehydrated) => setMessages(rehydrated))
                                .catch(() => { /* non-fatal: live card path still works */ });
                        }
                        return processedMessages;
                    }
                }
            } catch {
                return null;
            }
        },
        [selectedEndpoint, setMessages, workspaceId]
    );

    return { getSession, getSessions };
};

export default useSessionLoader;
